import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query, serverTimestamp, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { AccessDenied, canAccess, isOrderInScope } from '../adminAccess';
import DatabaseError from '../components/DatabaseError';

function asDate(v) {
  if (!v) return null;
  if (v.toDate) return v.toDate();
  if (typeof v === 'string') return new Date(v);
  return null;
}

function orderLabel(order) {
  if (!order) return 'Unknown order';
  return `${order.orderNumber || order.id.slice(-6)} · ${order.deceased?.name || order.deceasedName || 'No deceased name'}`;
}

export default function CommunicationsPage({ user }) {
  const [messages, setMessages] = useState([]);
  const [orders, setOrders] = useState({});
  const [users, setUsers] = useState({});
  const [search, setSearch] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [adminBody, setAdminBody] = useState('');
  const [adminRecipientRole, setAdminRecipientRole] = useState('customer');
  const [dbError, setDbError] = useState('');

  useEffect(() => {
    const unsubMessages = onSnapshot(
      query(collection(db, 'order_messages'), orderBy('createdAt', 'desc')),
      snap => {
        setDbError('');
        setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      },
      err => {
        console.error(err);
        setDbError(err?.code === 'permission-denied'
          ? 'Permission denied. Please log out and log in again as the super admin.'
          : (err?.message || 'Unable to read messages.'));
      }
    );
    const unsubOrders = onSnapshot(collection(db, 'orders'), snap => {
      const map = {};
      snap.docs.forEach(d => { map[d.id] = { id: d.id, ...d.data() }; });
      setOrders(map);
    }, err => {
      console.error(err);
      setDbError(err?.code === 'permission-denied'
        ? 'Permission denied. Please log out and log in again as the super admin.'
        : (err?.message || 'Unable to read orders for messages.'));
    });
    const unsubUsers = onSnapshot(collection(db, 'users'), snap => {
      const map = {};
      snap.docs.forEach(d => { map[d.id] = { id: d.id, ...d.data() }; });
      setUsers(map);
    }, err => {
      console.error(err);
      setDbError(err?.code === 'permission-denied'
        ? 'Permission denied. Please log out and log in again as the super admin.'
        : (err?.message || 'Unable to read users for messages.'));
    });
    return () => { unsubMessages(); unsubOrders(); unsubUsers(); };
  }, []);

  const scopedMessages = messages.filter(m => isOrderInScope(user, orders[m.orderId]));
  const orderIds = Object.values(orders)
    .filter(o => isOrderInScope(user, o))
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
    .map(o => o.id);
  const filtered = scopedMessages.filter(m => {
    const q = search.toLowerCase();
    const hay = [
      m.body, m.senderName, m.senderRole, m.recipientRole,
      orderLabel(orders[m.orderId]),
      users[m.senderId]?.name, users[m.recipientId]?.name,
    ].join(' ').toLowerCase();
    return (!selectedOrderId || m.orderId === selectedOrderId) && (!q || hay.includes(q));
  });

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(m => {
      const key = m.orderId || 'no-order';
      map[key] = map[key] || [];
      map[key].push(m);
    });
    Object.values(map).forEach(list => list.sort((a, b) => (asDate(a.createdAt) || 0) - (asDate(b.createdAt) || 0)));
    return map;
  }, [filtered]);

  if (!canAccess(user, 'communications')) return <AccessDenied />;

  async function sendAdminNote(orderId) {
    const body = adminBody.trim();
    if (!body || !orderId) return;
    const order = orders[orderId];
    const recipientId = adminRecipientRole === 'worker' ? order.workerId : order.customerId;
    if (!recipientId) return;
    const senderName = user?.name || 'PakMazar Support';
    await addDoc(collection(db, 'order_messages'), {
      orderId,
      orderNumber: order.orderNumber || '',
      senderId: user?.email || 'admin',
      senderRole: 'admin',
      senderName,
      recipientId,
      recipientRole: adminRecipientRole,
      participantIds: [user?.email || 'admin', recipientId],
      type: 'admin_note',
      body,
      attachmentUrl: '',
      isRead: false,
      createdAt: serverTimestamp(),
    });
    await addDoc(collection(db, 'notifications'), {
      userId: recipientId,
      title: `Message from ${senderName}`,
      body,
      orderId,
      orderNumber: order.orderNumber || '',
      senderId: user?.email || 'admin',
      senderRole: 'admin',
      senderName,
      messageType: 'admin_note',
      isRead: false,
      createdAt: serverTimestamp(),
    });
    setAdminBody('');
  }

  return (
    <div className="page-shell">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="page-title">Messages</h1>
          <p className="text-sm text-gray-500 mt-1">All order messages between customers, workers, and admin.</p>
        </div>
      </div>

      <DatabaseError message={dbError} />

      <div className="flex gap-3 mb-6">
        <input className="input-field flex-1" placeholder="Search messages, sender, order..." value={search} onChange={e => setSearch(e.target.value)} />
        <select className="input-field w-80" value={selectedOrderId} onChange={e => setSelectedOrderId(e.target.value)}>
          <option value="">All orders</option>
          {orderIds.map(id => <option key={id} value={id}>{orderLabel(orders[id])}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-4">
          {Object.entries(grouped).map(([orderId, list]) => (
            <div key={orderId} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 bg-gray-50 border-b border-gray-100">
                <p className="font-bold text-gray-900">{orderLabel(orders[orderId])}</p>
                <p className="text-xs text-gray-500">{list.length} messages</p>
              </div>
              <div className="p-5 space-y-3 max-h-[420px] overflow-y-auto">
                {list.map(m => {
                  const d = asDate(m.createdAt);
                  return (
                    <div key={m.id} className="rounded-xl border border-gray-100 p-3">
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <p className="text-sm font-semibold text-gray-900">
                          {m.senderName || users[m.senderId]?.name || m.senderRole || 'Sender'}
                          <span className="text-xs text-gray-400 font-normal"> → {users[m.recipientId]?.name || m.recipientRole || 'Recipient'}</span>
                        </p>
                        <span className="text-xs text-gray-400">{d ? d.toLocaleString() : '—'}</span>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{m.body}</p>
                      {m.type && <span className="badge bg-primary/10 text-primary mt-2">{m.type}</span>}
                      {m.attachmentUrl && <a className="block text-xs text-blue-600 underline mt-2" href={m.attachmentUrl} target="_blank" rel="noreferrer">Open attachment</a>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="bg-white rounded-2xl p-12 text-center text-gray-400">No messages found.</div>}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 h-fit">
          <h2 className="font-bold text-gray-900 mb-2">Send admin message</h2>
          <p className="text-xs text-gray-500 mb-4">Send an official PakMazar message to the customer or assigned worker.</p>
          <select className="input-field w-full mb-3" value={selectedOrderId} onChange={e => setSelectedOrderId(e.target.value)}>
            <option value="">Select order</option>
            {orderIds.map(id => <option key={id} value={id}>{orderLabel(orders[id])}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button className={adminRecipientRole === 'customer' ? 'btn-primary' : 'btn-secondary'} onClick={() => setAdminRecipientRole('customer')}>Customer</button>
            <button className={adminRecipientRole === 'worker' ? 'btn-primary' : 'btn-secondary'} onClick={() => setAdminRecipientRole('worker')}>Worker</button>
          </div>
          <textarea className="input-field w-full min-h-[130px] mb-3" value={adminBody} onChange={e => setAdminBody(e.target.value)} placeholder="Write message to customer..." />
          {selectedOrderId && adminRecipientRole === 'worker' && !orders[selectedOrderId]?.workerId && <p className="text-xs text-red-600 mb-3">Assign a worker to this order first.</p>}
          <button className="btn-primary w-full" disabled={!selectedOrderId || !adminBody.trim() || (adminRecipientRole === 'worker' && !orders[selectedOrderId]?.workerId)} onClick={() => sendAdminNote(selectedOrderId)}>Send to {adminRecipientRole}</button>
        </div>
      </div>
    </div>
  );
}
